import {
  AlertCircle, AlertTriangle, Button, Calendar,
  ChevronLeft,
  ChevronRight,
  Clock, Input, Maximize2,
  Minimize2,
  Plus,
  RefreshCw, Select, Trash2, Building2
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  subMonths,
} from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { enUS, fr } from "date-fns/locale";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AddAvailabilityInlineFormData,
  useAddAvailabilityInlineSchema,
} from "../../../schemas/availability.schema";
import doctorApi from "../../../services/doctor.api";
import {
  Doctor,
  TimeSlot,
  TimeSlotStatusEnum,
} from "../../../types/doctor.definition";
import { LicenceGate } from "../../atoms/LicenceGate";
import { AvailabilityFormData } from "../AvailabilityForm/AvailabilityForm";
import { getDateOfWeek } from "../../../utils/date.utils";
import { findOverlappingSlots } from "../../../utils/slot-overlap.utils";

interface DoctorAvailabilitySelectorProps {
  doctorId: string;
  doctor?: Doctor;
  onSelectSlot: (slotId: number, startTime: string) => void;
  selectedSlotId: number | null;
  disabled?: boolean;
  onDeleteSlot?: (slot: TimeSlot) => void;
  showDeleteButton?: boolean;
  onAddAvailability?: (data: AvailabilityFormData) => Promise<void>;
  isSubmittingAvailability?: boolean;
  onDeleteByDateRange?: () => void;
  // When set, slots whose healthcareFacilityId differs from this facility are
  // marked as belonging to another facility and can be neither selected nor
  // deleted here (MED-1648: a manager/secretary books only in their facility).
  ownFacilityId?: number;
  // When set (booking), available slots starting sooner than this many hours
  // from now are hidden: medecine360-api refuses them (@FutureTime, 4 h).
  minBookingHoursAhead?: number;
  // When true (booking), slots of another facility are not shown at all, and
  // their days get no availability dot: they cannot be booked here.
  hideOtherFacilitySlots?: boolean;
  // Gates the "Add Availability" form behind the facility's licence status.
  hasLicence?: boolean;
  onLicenceRestrictedClick?: () => void;
}

export interface DoctorAvailabilitySelectorRef {
  refresh: () => void;
}

const DAYS_OF_WEEK = [
  { value: "MONDAY", labelKey: "availability.days.monday" },
  { value: "TUESDAY", labelKey: "availability.days.tuesday" },
  { value: "WEDNESDAY", labelKey: "availability.days.wednesday" },
  { value: "THURSDAY", labelKey: "availability.days.thursday" },
  { value: "FRIDAY", labelKey: "availability.days.friday" },
  { value: "SATURDAY", labelKey: "availability.days.saturday" },
  { value: "SUNDAY", labelKey: "availability.days.sunday" },
];

const DURATION_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "60 minutes" },
  { value: "90", label: "90 minutes" },
  { value: "120", label: "120 minutes" },
];

export const DoctorAvailabilitySelector = React.forwardRef<
  DoctorAvailabilitySelectorRef,
  DoctorAvailabilitySelectorProps
>(
  (
    {
      doctorId,
      doctor: _doctor,
      onSelectSlot,
      selectedSlotId,
      disabled = false,
      onDeleteSlot,
      showDeleteButton = false,
      onAddAvailability,
      isSubmittingAvailability = false,
      onDeleteByDateRange,
      ownFacilityId,
      minBookingHoursAhead,
      hideOtherFacilitySlots = false,
      hasLicence = true,
      onLicenceRestrictedClick,
    },
    ref,
  ) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language?.startsWith("en") ? enUS : fr;
    const schema = useAddAvailabilityInlineSchema();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      formState: { errors: formErrors, isValid, isDirty },
    } = useForm<AddAvailabilityInlineFormData>({
      resolver: zodResolver(schema),
      mode: "onChange",
      defaultValues: {
        isRecurring: false,
        date: "",
        startDate: "",
        endDate: "",
        startTime: "09:00",
        endTime: "17:00",
        duration: "30",
        daysOfWeek: [],
      },
    });

    // MED-1687: react-hook-form only reveals the errors of the field being edited, so the
    // fields below declare `deps`: a start time after the end time, or a recurring range
    // with no day ticked, now shows its message instead of a silently disabled button.
    const isRecurring = watch("isRecurring");
    const daysOfWeek = watch("daysOfWeek");
    const formDate = watch("date");
    const formStartDate = watch("startDate");
    const formEndDate = watch("endDate");
    const formStartTime = watch("startTime");
    const formEndTime = watch("endTime");
    // MED-1687: past days are greyed out in the date pickers (slot dates are Africa/Dakar)
    const todayInDakar = formatInTimeZone(new Date(), "Africa/Dakar", "yyyy-MM-dd");

    // MED-1687: the doctor's existing slots (any facility, any status) that the requested
    // availability overlaps. medecine360-api refuses the request with AVAILABILITY_ALREADY_EXISTS;
    // the calendar only shows this facility, so the form lists them and blocks the submit.
    const [overlappingSlots, setOverlappingSlots] = useState<TimeSlot[]>([]);
    const [overlapCheckKey, setOverlapCheckKey] = useState(0);
    useEffect(() => {
      const startDate = isRecurring ? formStartDate : formDate;
      const endDate = isRecurring ? formEndDate : formDate;
      const days = isRecurring ? daysOfWeek ?? [] : [getDateOfWeek(formDate ?? "") ?? ""];
      if (
        !doctorId ||
        !startDate ||
        !endDate ||
        endDate < startDate ||
        !formStartTime ||
        !formEndTime ||
        formEndTime <= formStartTime ||
        days.filter(Boolean).length === 0
      ) {
        setOverlappingSlots([]);
        return;
      }
      let cancelled = false;
      const timer = setTimeout(async () => {
        try {
          const slots = await doctorApi.getDoctorTimeSlotsByDateRange(doctorId, startDate, endDate);
          if (!cancelled) {
            setOverlappingSlots(
              findOverlappingSlots(slots || [], {
                startDate,
                endDate,
                startTime: formStartTime,
                endTime: formEndTime,
                daysOfWeek: days,
              }),
            );
          }
        } catch (err) {
          // The API still refuses the request; the check only saves a round trip.
          console.error("Error checking existing slots:", err);
          if (!cancelled) setOverlappingSlots([]);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }, [
      doctorId,
      isRecurring,
      formDate,
      formStartDate,
      formEndDate,
      formStartTime,
      formEndTime,
      daysOfWeek,
      overlapCheckKey,
    ]);

    // Use a ref to track the last month we fetched data for
    const lastFetchedMonthRef = useRef<string | null>(null);
    const isFetchingRef = useRef<boolean>(false);

    // Clear time slots and cache when doctor changes
    useEffect(() => {
      setTimeSlots([]);
      lastFetchedMonthRef.current = null;
      setError(null);
      isFetchingRef.current = false;
      setSelectedDate(null);
    }, [doctorId]);

    // Fetch doctor time slots for the entire month
    const fetchDoctorTimeSlots = useCallback(
      async (monthDate: Date, forceRefresh = false) => {
        if (!doctorId || !monthDate || isNaN(monthDate.getTime())) return;

        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);
        const startDate = format(monthStart, "yyyy-MM-dd");
        const endDate = format(monthEnd, "yyyy-MM-dd");
        const monthKey = `${doctorId}-${startDate}-${endDate}`;

        if (!forceRefresh && monthKey === lastFetchedMonthRef.current) return;
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
          const response = await doctorApi.getDoctorTimeSlotsByDateRange(
            doctorId,
            startDate,
            endDate,
          );
          setTimeSlots(response || []);
          lastFetchedMonthRef.current = monthKey;
        } catch (error) {
          console.error("Error fetching doctor time slots:", error);
          setError(t("availability.loadDoctorAvailabilityError"));
        } finally {
          setIsLoading(false);
          isFetchingRef.current = false;
        }
      },
      [doctorId, t],
    );

    // Expose refresh function via ref
    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          if (currentMonth) {
            fetchDoctorTimeSlots(currentMonth, true);
          }
          // MED-1687: a slot deleted (or added) from the page may change the overlap warning
          setOverlapCheckKey((key) => key + 1);
        },
      }),
      [fetchDoctorTimeSlots, currentMonth],
    );

    // Fetch data when doctorId or month changes
    useEffect(() => {
      if (currentMonth && doctorId) {
        fetchDoctorTimeSlots(currentMonth, false);
      }
    }, [doctorId, currentMonth, fetchDoctorTimeSlots]);

    // Get dates with availability (for green dots)
    // Slot of another facility hidden in booking mode (MED-1653).
    const isHiddenOtherFacilitySlot = (slot: TimeSlot): boolean =>
      hideOtherFacilitySlots &&
      ownFacilityId != null &&
      slot.healthcareFacilityId !== ownFacilityId;

    const getDatesWithAvailability = (): Set<string> => {
      const datesSet = new Set<string>();
      timeSlots.forEach((slot) => {
        if (slot.slotDate && !isHiddenOtherFacilitySlot(slot)) {
          datesSet.add(slot.slotDate);
        }
      });
      return datesSet;
    };

    // Slot start as an instant (slot dates and times are Africa/Dakar).
    const slotStart = (slot: TimeSlot): Date =>
      fromZonedTime(`${slot.slotDate}T${slot.slotStartTime}`, "Africa/Dakar");

    // Available slot that the API would refuse: it starts too soon.
    const isTooSoon = (slot: TimeSlot): boolean =>
      minBookingHoursAhead != null &&
      slot.status === TimeSlotStatusEnum.AVAILABLE &&
      !slot.isBooked &&
      slotStart(slot).getTime() <
        Date.now() + minBookingHoursAhead * 60 * 60 * 1000;

    // Get slots for selected date
    const getSlotsForSelectedDate = (): TimeSlot[] => {
      if (!selectedDate) return [];
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      return timeSlots.filter(
        (slot) =>
          slot.slotDate === dateStr &&
          !isTooSoon(slot) &&
          !isHiddenOtherFacilitySlot(slot),
      );
    };

    // Get status text for a slot
    const getStatusText = useCallback(
      (slot: TimeSlot, isPastSlot: boolean): string => {
        const isBooked =
          slot.status === TimeSlotStatusEnum.BOOKED || slot.isBooked;
        const isExpired = slot.status === TimeSlotStatusEnum.EXPIRED;

        if (isBooked) {
          return t("availability.confirmed");
        }
        if (
          slot.status === TimeSlotStatusEnum.AVAILABLE &&
          !isPastSlot &&
          !isExpired
        ) {
          return t("availability.available");
        }
        if (slot.status === TimeSlotStatusEnum.CANCELLED) {
          return t("availability.cancelled");
        }
        if (slot.status === TimeSlotStatusEnum.EXPIRED || isPastSlot) {
          return t("availability.expired");
        }
        return t("availability.available");
      },
      [t],
    );

    // Generate calendar days
    const calendarDays = eachDayOfInterval({
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth),
    });

    // Get first day of month to calculate offset
    const firstDayOfMonth = getDay(startOfMonth(currentMonth));
    const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Monday = 0

    const datesWithAvailability = getDatesWithAvailability();

    // Handle day toggle for daysOfWeek field
    const handleDayToggle = (day: string) => {
      const current = daysOfWeek ?? [];
      const updated = current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day];
      setValue("daysOfWeek", updated, { shouldValidate: true });
    };

    // Handle form submission (RHF validates via Zod before calling this)
    const handleFormSubmit = handleSubmit(async (data) => {
      if (!onAddAvailability || !doctorId) return;

      try {
        const submitData: AvailabilityFormData = {
          startDate: data.isRecurring ? data.startDate! : data.date!,
          endDate: data.isRecurring ? data.endDate! : data.date!,
          startTime: data.startTime,
          endTime: data.endTime,
          daysOfWeek: data.isRecurring ? data.daysOfWeek : [],
          isRecurring: data.isRecurring,
          slotDurationMinutes: parseInt(data.duration, 10),
        };

        await onAddAvailability(submitData);
        reset();
        fetchDoctorTimeSlots(currentMonth, true);
        setOverlapCheckKey((key) => key + 1);
      } catch (err) {
        console.error("Error submitting availability:", err);
      }
    });

    const isSlotInOwnFacility = (slot: TimeSlot) =>
      ownFacilityId == null || slot.healthcareFacilityId === ownFacilityId;

    const handleSelectSlot = (slot: TimeSlot) => {
      const isBooked =
        slot.status === TimeSlotStatusEnum.BOOKED ||
        slot.status === TimeSlotStatusEnum.CANCELLED ||
        slot.status === TimeSlotStatusEnum.EXPIRED ||
        slot.isBooked ||
        !slot.isActive;
      if (isBooked || disabled || !isSlotInOwnFacility(slot) || isTooSoon(slot))
        return;

      const dateStr = slot.slotDate;
      const timeStr = slot.slotStartTime;
      const appointmentTime = fromZonedTime(
        `${dateStr}T${timeStr}:00`,
        "Africa/Dakar"
      ).toISOString();

      onSelectSlot(slot.id, appointmentTime);
    };

    const isDateInPast = (date: Date): boolean => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    };

    // MED-1687: with a minimum notice, a day is only active while part of it (Africa/Dakar)
    // is still at least minBookingHoursAhead hours away — today goes grey in the evening.
    const isDayInactive = (day: Date): boolean =>
      isDateInPast(day) ||
      (minBookingHoursAhead != null &&
        fromZonedTime(`${format(day, "yyyy-MM-dd")}T23:59:59`, "Africa/Dakar").getTime() <
          Date.now() + minBookingHoursAhead * 60 * 60 * 1000);

    if (!doctorId) {
      return (
        <div className="p-4 text-center text-gray-500 rounded-md border border-gray-200">
          {t("availability.selectDoctorToView")}
        </div>
      );
    }

    const selectedDateSlots = getSlotsForSelectedDate();

    return (
      <div className="space-y-6">
        {/* Add Availability Form Section */}
        {onAddAvailability && (
          <LicenceGate
            isLicenced={hasLicence}
            tooltipText={t("licence.requiredTooltip")}
            onRestrictedClick={() => onLicenceRestrictedClick?.()}
          >
            <div className="p-6 bg-white rounded-xl border shadow-sm border-neutral-200">
              <h2 className="mb-4 text-xl font-semibold text-neutral-900">
                {t("availability.addAvailability")}
              </h2>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Recurring Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isRecurring"
                    {...register("isRecurring", { deps: ["daysOfWeek"] })}
                    className="mr-2 w-4 h-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
                    disabled={isSubmittingAvailability}
                  />
                  <label
                    htmlFor="isRecurring"
                    className="text-sm font-medium cursor-pointer text-neutral-700"
                  >
                    {t("availability.createRecurringSlots")}
                  </label>
                </div>

                {/* Date Inputs */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {isRecurring ? (
                    <>
                      <Input
                        label={t("availability.startDate")}
                        type="date"
                        min={todayInDakar}
                        {...register("startDate", { deps: ["endDate", "daysOfWeek"] })}
                        disabled={isSubmittingAvailability}
                        leftIcon={<Calendar className="w-4 h-4" />}
                        error={formErrors.startDate?.message}
                      />
                      <Input
                        label={t("availability.endDate")}
                        type="date"
                        min={formStartDate && formStartDate > todayInDakar ? formStartDate : todayInDakar}
                        {...register("endDate", { deps: ["daysOfWeek"] })}
                        disabled={isSubmittingAvailability}
                        leftIcon={<Calendar className="w-4 h-4" />}
                        error={formErrors.endDate?.message}
                      />
                    </>
                  ) : (
                    <Input
                      label={t("availability.date")}
                      type="date"
                      min={todayInDakar}
                      {...register("date")}
                      disabled={isSubmittingAvailability}
                      leftIcon={<Calendar className="w-4 h-4" />}
                      error={formErrors.date?.message}
                    />
                  )}

                  <Input
                    label={t("availability.startTime")}
                    type="time"
                    {...register("startTime", { deps: ["endTime"] })}
                    disabled={isSubmittingAvailability}
                    leftIcon={<Clock className="w-4 h-4" />}
                    error={formErrors.startTime?.message}
                  />
                  <Input
                    label={t("availability.endTime")}
                    type="time"
                    {...register("endTime")}
                    disabled={isSubmittingAvailability}
                    leftIcon={<Clock className="w-4 h-4" />}
                    error={formErrors.endTime?.message}
                  />
                </div>

                {/* Days of Week (only for recurring) */}
                {isRecurring && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-neutral-700">
                      {t("availability.daysOfWeek")}
                    </label>
                    {formErrors.daysOfWeek && (
                      <p className="mb-2 text-sm text-error-600">
                        {formErrors.daysOfWeek.message}
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                      {DAYS_OF_WEEK.map((day) => (
                        <label
                          key={day.value}
                          className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${daysOfWeek?.includes(day.value)
                              ? "bg-primary-50 border-primary-300 text-primary-700"
                              : "bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={daysOfWeek?.includes(day.value) ?? false}
                            onChange={() => handleDayToggle(day.value)}
                            disabled={isSubmittingAvailability}
                            className="mr-2 w-4 h-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
                          />
                          <span className="text-sm">{t(day.labelKey)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Duration */}
                <div>
                  <Select
                    label={t("availability.duration")}
                    value={watch("duration")}
                    onChange={(value) => setValue("duration", value as string)}
                    disabled={isSubmittingAvailability}
                    options={DURATION_OPTIONS}
                  />
                </div>

                {overlappingSlots.length > 0 && (
                  <div
                    role="alert"
                    className="p-4 rounded-lg border bg-warning-50 border-warning-200"
                  >
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="flex-shrink-0 mt-0.5 w-5 h-5 text-warning-600" />
                      <div className="text-sm">
                        <p className="font-semibold text-neutral-900">
                          {t("availability.overlap.title")}
                        </p>
                        <p className="mt-1 text-neutral-700">
                          {t("availability.overlap.message", { count: overlappingSlots.length })}
                        </p>
                        <ul className="mt-2 space-y-1 text-neutral-700">
                          {overlappingSlots.slice(0, 5).map((slot) => (
                            <li key={slot.id}>
                              {format(parseISO(slot.slotDate), "EEEE d MMMM yyyy", { locale: dateLocale })}
                              {" · "}
                              {slot.slotStartTime.slice(0, 5)} - {slot.slotEndTime.slice(0, 5)}
                              {" · "}
                              {getStatusText(slot, isDateInPast(parseISO(slot.slotDate)))}
                              {ownFacilityId != null &&
                                slot.healthcareFacilityId !== ownFacilityId &&
                                ` · ${t("availability.otherFacilitySlot")}`}
                            </li>
                          ))}
                        </ul>
                        {overlappingSlots.length > 5 && (
                          <p className="mt-1 text-neutral-500">
                            {t("availability.overlap.more", { count: overlappingSlots.length - 5 })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="ews-primary"
                    disabled={
                      isSubmittingAvailability ||
                      !isValid ||
                      !isDirty ||
                      overlappingSlots.length > 0
                    }
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    {t("availability.addAvailability")}
                  </Button>
                </div>
              </form>
            </div>
          </LicenceGate>
        )}

        {/* Timezone Banner */}
        <div className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Clock className="mr-3 w-5 h-5 text-blue-600" />
          <p className="text-sm text-blue-800">
            {t("availability.timezoneInfo")}
          </p>
        </div>

        {/* Calendar Section */}
        <div
          className={`p-6 bg-white rounded-xl border shadow-sm border-neutral-200 transition-all  ${isFullscreen
              ? "overflow-y-auto fixed inset-0 z-50 rounded-none"
              : "relative"
            }`}
        >
          {/* MED-1687: wraps on a phone, the delete-by-range button was pushed off screen */}
          <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-neutral-900">
              {t("availability.calendar")}
            </h2>
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                disabled={disabled || isLoading}
                title={
                  isFullscreen
                    ? t("availability.exitFullscreen")
                    : t("availability.fullscreen")
                }
              >
                {isFullscreen ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fetchDoctorTimeSlots(currentMonth, true)}
                disabled={disabled || isLoading}
              >
                <RefreshCw
                  className={`mr-2 w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                />
                {t("availability.refresh")}
              </Button>
              {onDeleteByDateRange && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onDeleteByDateRange}
                  disabled={disabled || isLoading}
                  title={t("availability.deleteByDateRange")}
                  className="p-0 w-9 h-9"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-center p-3 mb-4 text-red-700 bg-red-50 rounded-lg border border-red-200">
              <AlertCircle className="mr-2 w-4 h-4" />
              {error}
            </div>
          )}

          <div
            className={`grid gap-6 ${isFullscreen
                ? "grid-cols-1 xl:grid-cols-2"
                : "grid-cols-1 xl:grid-cols-2"
              }`}
          >
            {/* Left Panel - Calendar */}
            <div>
              <div className="overflow-hidden rounded-lg border border-neutral-200">
                {/* Calendar Header */}
                <div className="flex justify-between items-center p-4 border-b bg-neutral-50 border-neutral-200">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    disabled={disabled || isLoading}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="font-semibold text-neutral-900">
                    {format(currentMonth, "MMMM yyyy", { locale: dateLocale })}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    disabled={disabled || isLoading}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Calendar Grid */}
                <div className="p-4">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {(
                      ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const
                    ).map((key) => (
                      <div
                        key={key}
                        className="p-2 text-xs font-medium text-center text-neutral-600"
                      >
                        {t(`availability.weekDayShort.${key}`)}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells for offset */}
                    {Array.from({ length: offset }).map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square" />
                    ))}

                    {/* Calendar days */}
                    {calendarDays.map((day) => {
                      const dateStr = format(day, "yyyy-MM-dd");
                      const hasAvailability =
                        datesWithAvailability.has(dateStr);
                      const isSelected =
                        selectedDate && isSameDay(day, selectedDate);
                      const isPast = isDayInactive(day);
                      const isCurrentMonth = isSameMonth(day, currentMonth);

                      return (
                        <button
                          key={day.toISOString()}
                          // MED-1687: inside the booking form, a plain button submitted it
                          type="button"
                          onClick={() => setSelectedDate(day)}
                          disabled={isPast || !isCurrentMonth}
                          className={`
                            aspect-square p-2 text-sm rounded-lg transition-colors
                            ${isSelected
                              ? "bg-primary-600 text-white font-semibold"
                              : isPast || !isCurrentMonth
                                ? "text-neutral-300 cursor-not-allowed"
                                : "text-neutral-700 hover:bg-neutral-100"
                            }
                          `}
                        >
                          <div className="flex flex-col justify-center items-center h-full">
                            <span>{format(day, "d")}</span>
                            {hasAvailability && !isPast && (
                              <div className="w-1.5 h-1.5 min-w-[6px] min-h-[6px] mt-1 rounded-full bg-green-500 shrink-0" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Selected Date Slots */}
            <div className="rounded-lg border border-neutral-200">
              {selectedDate ? (
                <div className="p-6">
                  <h3 className="mb-4 text-lg font-semibold text-neutral-900">
                    {format(selectedDate, "EEEE, d MMMM yyyy", { locale: dateLocale })}
                  </h3>

                  {isLoading ? (
                    <div className="py-8 text-center text-neutral-500">
                      {t("availability.loading")}
                    </div>
                  ) : selectedDateSlots.length === 0 ? (
                    <div className="py-8 text-center text-neutral-500">
                      <Calendar className="mx-auto mb-3 w-12 h-12 text-neutral-300" />
                      <p className="text-sm">
                        {t("availability.noSlotsForDate")}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h4 className="mb-4 text-lg font-semibold text-neutral-900">
                        {t("availability.timeSlots")}
                      </h4>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {selectedDateSlots.map((slot) => {
                          const isPastSlot = isDateInPast(selectedDate!);
                          const isBooked =
                            slot.status === TimeSlotStatusEnum.BOOKED ||
                            slot.isBooked;
                          const isExpired =
                            slot.status === TimeSlotStatusEnum.EXPIRED;
                          const isAvailable =
                            slot.status === TimeSlotStatusEnum.AVAILABLE &&
                            slot.isActive &&
                            !isBooked &&
                            !isPastSlot &&
                            !isExpired;
                          const isOwnFacility = isSlotInOwnFacility(slot);

                          // Format time as HH:MM
                          const formatTime = (time: string) => {
                            return time.substring(0, 5); // Get HH:MM from HH:MM:SS
                          };

                          // Determine color scheme based on status
                          const getSlotStyles = () => {
                            if (isPastSlot || isExpired) {
                              // Gray for past/expired slots
                              return {
                                container:
                                  "bg-gray-100 border-gray-300 cursor-not-allowed opacity-60",
                                time: "text-gray-500",
                                status: "text-gray-500",
                              };
                            }
                            if (isBooked) {
                              // Blue for confirmed/booked slots
                              return {
                                container:
                                  selectedSlotId === slot.id
                                    ? "bg-blue-600 border-blue-700 shadow-md"
                                    : "bg-blue-50 border-blue-200 hover:bg-blue-100 cursor-pointer",
                                time:
                                  selectedSlotId === slot.id
                                    ? "text-white"
                                    : "text-blue-600",
                                status:
                                  selectedSlotId === slot.id
                                    ? "text-blue-100"
                                    : "text-blue-600",
                              };
                            }
                            if (isAvailable && !isOwnFacility) {
                              // Available, but in another facility: not bookable here
                              return {
                                container:
                                  "bg-gray-100 border-gray-300 cursor-not-allowed opacity-60",
                                time: "text-gray-500",
                                status: "text-gray-500",
                              };
                            }
                            if (isAvailable) {
                              // Green for available slots
                              return {
                                container:
                                  selectedSlotId === slot.id
                                    ? "bg-green-600 border-green-700 shadow-md"
                                    : "bg-green-50 border-green-200 hover:bg-green-100 cursor-pointer",
                                time:
                                  selectedSlotId === slot.id
                                    ? "text-white"
                                    : "text-green-600",
                                status:
                                  selectedSlotId === slot.id
                                    ? "text-green-100"
                                    : "text-green-600",
                              };
                            }
                            // Default gray for cancelled or inactive
                            return {
                              container:
                                "bg-gray-100 border-gray-300 cursor-not-allowed opacity-60",
                              time: "text-gray-500",
                              status: "text-gray-500",
                            };
                          };

                          const styles = getSlotStyles();
                          const isClickable =
                            isAvailable && isOwnFacility && !disabled;

                          return (
                            <div
                              key={slot.id}
                              data-testid={`time-slot-${slot.id}`}
                              aria-disabled={!isClickable}
                              className={`relative p-3 sm:p-4 rounded-lg border transition-all group min-w-0 ${styles.container
                                } ${disabled ? "opacity-50" : ""}`}
                              onClick={() =>
                                isClickable && handleSelectSlot(slot)
                              }
                            >
                              {!isOwnFacility && (
                                <div
                                  className="absolute top-1 left-1 p-0.5 text-amber-600 bg-white rounded-full shadow-sm"
                                  title={t("availability.otherFacilitySlot")}
                                >
                                  <Building2 className="w-3 h-3" />
                                </div>
                              )}
                              <div className="flex flex-col justify-center items-center w-full min-w-0 text-center">
                                <div
                                  className={`text-xs font-bold mb-1 ${styles.time} whitespace-nowrap`}
                                >
                                  {formatTime(slot.slotStartTime)}
                                </div>
                                <div
                                  className={`text-xs px-2 py-0.5 ${styles.status} hidden xl:block truncate w-full`}
                                >
                                  {getStatusText(slot, isPastSlot)}
                                </div>
                              </div>
                              {showDeleteButton &&
                                onDeleteSlot &&
                                isAvailable &&
                                !isPastSlot &&
                                isOwnFacility && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteSlot(slot);
                                    }}
                                    className="absolute top-1 right-1 p-1 text-red-600 rounded opacity-0 transition-opacity hover:bg-red-100 group-hover:opacity-100"
                                    title={t("availability.deleteSlot")}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-6 text-center text-neutral-500">
                  <Calendar className="mb-4 w-16 h-16 text-neutral-300" />
                  <p className="text-sm">{t("availability.selectDate")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

DoctorAvailabilitySelector.displayName = "DoctorAvailabilitySelector";
